import { Injectable, inject } from '@angular/core';
import { FirebaseApp, getApp, initializeApp } from '@angular/fire/app';
import {
  Firestore,
  getFirestore,
  initializeFirestore,
} from '@angular/fire/firestore';
import { Storage, getStorage } from '@angular/fire/storage';
import { FirebaseClientConfig } from '@models';

/**
 * Wraps the active Firebase Firestore and Storage instances for the current tenant.
 *
 * Standard tenants → both `db` and `storage` point to the shared master Firebase project.
 * Enterprise tenants → after switchToProject() is called (triggered by TenantFeaturesService
 * when TenantConfig.firebaseClientConfig is present), `db` and `storage` point to the
 * tenant's dedicated Firebase project.
 *
 * Services that operate on tenant data (clubs, teams, users, matches…) inject this service
 * and use `db` / `storage` getters.
 * Services that operate on platform-level data (TenantConfig, billing, support tickets)
 * should use `masterDb` to always hit the shared master project.
 */
@Injectable({ providedIn: 'root' })
export class TenantFirebaseService {
  private readonly defaultFirestore = inject(Firestore);
  private readonly defaultStorage = inject(Storage);

  private _db!: Firestore;
  private _storage!: Storage;
  private _activeProjectId: string | null = null;

  constructor() {
    this._db = this.defaultFirestore;
    this._storage = this.defaultStorage;
  }

  /** Shared-project Firestore — always points to the master Firebase project. */
  get masterDb(): Firestore {
    return this.defaultFirestore;
  }

  /** Active Firestore — master project for standard tenants, dedicated project for enterprise. */
  get db(): Firestore {
    return this._db;
  }

  /** Active Storage — master project for standard tenants, dedicated project for enterprise. */
  get storage(): Storage {
    return this._storage;
  }

  /** Firebase project ID currently active. Null means the shared master project. */
  get activeProjectId(): string | null {
    return this._activeProjectId;
  }

  /**
   * Switches Firestore and Storage to the enterprise tenant's dedicated Firebase project.
   * No-op if already pointing to the same project.
   *
   * Called by TenantFeaturesService as soon as TenantConfig.firebaseClientConfig is loaded.
   * By the time any data component renders, the switch is already in effect.
   */
  switchToProject(config: FirebaseClientConfig, projectId: string): void {
    if (this._activeProjectId === projectId) return;

    let app: FirebaseApp;
    try {
      // Secondary app was already initialized in a previous session (e.g. hard refresh).
      app = getApp(projectId);
      this._db = getFirestore(app);
    } catch {
      // First initialization — create the named secondary Firebase app.
      app = initializeApp(config, projectId);
      // initializeFirestore must be called before getFirestore on a fresh app.
      this._db = initializeFirestore(app, { ignoreUndefinedProperties: true });
    }

    this._storage = getStorage(app);
    this._activeProjectId = projectId;
  }
}
